import { blobMaxBrushRadius, CLOUD_ACCRETE_LUMPINESS, CLOUD_ACCRETE_RADIUS, CLOUD_SEED_RADIUS } from './blob_sdf/constants'
import type { VoxelInteractionPolicy, VoxelTypeMetadata } from './registry'

/*
 * What each voxel type is, for the tools and the document: plain data, no renderer. The registry's
 * policy lookups (src/voxels/registry.tsx: `supportsColor`, `placementPolicy`, `strokePolicy`, ...)
 * read this table, so they answer the same before and after the renderers have loaded. How a type
 * is drawn (its component, packing and palette preview) is its `VoxelSpec`, registered by the
 * renderer (src/voxels/index.tsx).
 *
 * Order matters: it is the order of `getRegisteredTypes()` (the palette catalog).
 */

/** The interaction policy of each bulk material (plan §5 "Worlds to try first"). */
export const BULK_INTERACTION_POLICY: Readonly<Record<'sand' | 'clay', VoxelInteractionPolicy>> = Object.freeze({
  sand: {
    brushSize: 'volume',
    placement: { target: 'cell' },
    stroke: { verb: 'fill', lumpVoxels: 2, lumpCell: 4 },
    digScope: 'self',
    maxRadius: 32,
  },
  clay: {
    brushSize: 'volume',
    placement: { target: 'cell' },
    stroke: { verb: 'fill', lumpVoxels: 1, lumpCell: 3, smearOnSameType: true },
    digScope: 'self',
    maxRadius: 24,
  },
})

/** Things stuck onto a surface (eyes, roads, trees, water): placed on a backed face, never a surface themselves. */
const onSurface = (allowedFaces: 'any' | ['y+']): VoxelInteractionPolicy => ({
  brushSize: 'surface',
  placement: { target: 'surface', allowedFaces, requireBackingSurface: true, allowDocumentPlanes: true },
  providesPlacementSurface: false,
})

const KINDS: ReadonlyArray<readonly [string, VoxelTypeMetadata]> = [
  ['googly_eye', { usesSurfaceOrientation: true, preferSeedFace: true, supportsColor: false, interaction: onSurface('any') }],
  ['ice_cube', { supportsColor: true }],
  ['road', { usesSurfaceOrientation: true, preferSeedFace: true, supportsColor: true, interaction: onSurface('any') }],
  ['tube', {
    usesSurfaceOrientation: true,
    preferSeedFace: true,
    forceStraightScribble: false,
    rectanglePlacement: 'outline',
    supportsColor: true,
    needsOrthogonalConnectivity: true,
    interaction: { brushSize: 'none' },
  }],
  ['sphere', { supportsColor: true }],
  ['diamond', {
    supportsColor: true,
    // hasSupportBelow() in diamondRenderer reads the voxel beneath each diamond (the grouped path;
    // the chunked runtime's 'any' codec covers it there, SparseFamilyIndex.ts).
    renderReadsNeighbors: true,
    interaction: { providesPlacementSurface: false },
  }],
  ['heart', { supportsColor: true, usesSurfaceOrientation: true, preferSeedFace: true }],
  ['tree', { supportsColor: true, interaction: onSurface(['y+']) }],
  ['glass_cube', { supportsColor: true }],
  ['water', { supportsColor: true, interaction: onSurface(['y+']) }],
  // One stroke may not exceed the blob radius cap (materials-perf-plan Q5): the SDF cost grows with
  // the component, and the UI slider is clamped at stroke start through `effectiveBrushRadius`.
  ['blob', { supportsColor: true, interaction: { maxRadius: blobMaxBrushRadius() } }],
  ['cloud', {
    supportsColor: true,
    interaction: {
      // Cloud accretes at a fixed radius (CLOUD_ACCRETE_RADIUS), so the radius slider is hidden (Q4).
      brushSize: 'none',
      voxelHitPlacement: 'hit',
      sameTypeExpansionRadius: CLOUD_ACCRETE_RADIUS,
      sameTypeSeedRadius: CLOUD_SEED_RADIUS,
      sameTypeExpansionLumpiness: CLOUD_ACCRETE_LUMPINESS,
    },
  }],
  ['solid', { supportsColor: true }],
  // Only the composed grid's own cells decide a bulk cell's rendering (occlusion comes from the
  // mesher's LUT, not from the component), so the grouping gate may skip bulk-only changes.
  ['sand', { supportsColor: true, interaction: BULK_INTERACTION_POLICY.sand, renderReadsNeighbors: false }],
  ['clay', { supportsColor: true, interaction: BULK_INTERACTION_POLICY.clay, renderReadsNeighbors: false }],
  ['tint', {
    supportsColor: true,
    renderReadsNeighbors: false,
    interaction: {
      brushSize: 'volume',
      placement: { target: 'cell' },
      // Poured: a stroke writes empty cells only (also forced without the policy flag, brushPolicy.ts).
      stroke: { verb: 'fill' },
    },
  }],
]

const kinds = new Map<string, VoxelTypeMetadata>(KINDS)

/** A type's metadata, or undefined for a type Huebo does not know. `type` is canonical (registry). */
export function voxelKind(type: string): VoxelTypeMetadata | undefined {
  return kinds.get(type)
}

/** Every known voxel type, in palette order. */
export function voxelKindTypes(): string[] {
  return Array.from(kinds.keys())
}

/** Declare a type that is not in the table (tests, experiments); a later call replaces it. */
export function defineVoxelKind(type: string, metadata: VoxelTypeMetadata): void {
  kinds.set(type, metadata)
}
